import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inboxTasksService, InboxTasksResponse, CompleteTaskInput } from '../../services/inboxTasksService';
import { InboxTask } from '../../types/workflow';
import { STALE_TIME } from '../../config/staleTimeConfig';

// Query keys
export const inboxTaskKeys = {
  all: (params?: {
    status?: string;
    type?: string;
    priority?: string;
    scope?: 'my' | 'all';
    page?: number;
    limit?: number;
  }) => ['inboxTasks', params] as const,
  detail: (id: string) => ['inboxTasks', id] as const,
  myCount: () => ['inboxTasks', 'my-count'] as const,
};

// Get inbox tasks with filters
export function useInboxTasks(params?: {
  status?: string;
  type?: string;
  priority?: string;
  scope?: 'my' | 'all';
  page?: number;
  limit?: number;
  enabled?: boolean;
}) {
  const { enabled = true, ...queryParams } = params || {};

  return useQuery({
    queryKey: inboxTaskKeys.all(queryParams),
    queryFn: () => inboxTasksService.getTasks(queryParams),
    enabled, // Only fetch when explicitly enabled (default: true)
    staleTime: STALE_TIME.FREQUENT, // 30s - tasks change frequently during active use
    gcTime: 5 * 60 * 1000, // 5 minutes - frequent data cleanup
  });
}

// Paginated inbox tasks for the tasks view ("Charger plus"). Server-side scope
// (my/all) plus real pagination replaces the old page:1/limit:50 silent cap.
export function useInfiniteInboxTasks(params?: {
  status?: string;
  type?: string;
  priority?: string;
  scope?: 'my' | 'all';
  sort?: 'due';
  limit?: number;
  enabled?: boolean;
}) {
  const { enabled = true, limit = 20, ...rest } = params || {};

  return useInfiniteQuery({
    queryKey: ['inboxTasks', 'infinite', { ...rest, limit }] as const,
    queryFn: ({ pageParam }) => inboxTasksService.getTasks({ ...rest, limit, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.page - 1) * lastPage.limit + lastPage.tasks.length;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    enabled,
    staleTime: STALE_TIME.FREQUENT,
    // Poll in the background so a task that lands elsewhere shows up without a
    // manual refresh or refocus.
    refetchInterval: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// Reminder visibility for a task: last reminded, next automatic, count, paused.
// Polls on the same 60s cadence so "next in Xh" stays roughly accurate.
export function useReminderStatus(taskId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: ['inboxTasks', 'reminder-status', taskId] as const,
    queryFn: () => inboxTasksService.getReminderStatus(taskId!),
    enabled: enabled && !!taskId,
    staleTime: STALE_TIME.FREQUENT,
    refetchInterval: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// Authoritative count of the current user's pending inbox tasks (assigned +
// eligible pools), computed server-side. Not capped, unlike the old client tally.
export function useMyInboxCount(enabled: boolean = true) {
  return useQuery({
    queryKey: inboxTaskKeys.myCount(),
    queryFn: () => inboxTasksService.getMyPendingCount(),
    enabled,
    staleTime: STALE_TIME.FREQUENT,
    // Keep the sidebar badge live without a refocus (e.g. a task that bounced
    // back to the submitter, whose assignment email is intentionally suppressed).
    refetchInterval: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// Get single inbox task
// NOTE: Handle success/error notifications in component using try/catch with mutateAsync
// OPTIMISTIC UPDATE: UI updates instantly, rolls back on error
// Complete task (approve, reject, etc.)
export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CompleteTaskInput }) =>
      inboxTasksService.completeTask(id, input),

    // 1. Optimistic update: Update cache before API call
    onMutate: async ({ id, input }) => {
      // Cancel any outgoing refetches (so they don't overwrite optimistic update)
      await queryClient.cancelQueries({ queryKey: ['inboxTasks'] });

      // Snapshot previous value for rollback
      const previousQueries = queryClient.getQueriesData<InboxTasksResponse>({
        queryKey: ['inboxTasks']
      });

      // Determine new status based on action
      const newStatus = input.action === 'approve' ? 'completed' :
                       input.action === 'reject' ? 'rejected' :
                       input.action === 'complete' ? 'completed' :
                       'completed'; // default for 'upload'

      // Optimistically update all matching queries
      queryClient.setQueriesData<InboxTasksResponse>(
        { queryKey: ['inboxTasks'] },
        (old) => {
          // The prefix also covers the count, the infinite list, a task's
          // detail and its reminder status: only task lists are updated here.
          if (!old || !Array.isArray(old.tasks)) return old;

          // Find the task to check its old status
          const oldTask = old.tasks.find(task => task.id === id);
          const wasPending = oldTask?.status === 'pending';

          return {
            ...old,
            tasks: old.tasks.map(task =>
              task.id === id
                ? {
                    ...task,
                    status: newStatus,
                    // Optionally add completion timestamp
                    completedAt: new Date().toISOString()
                  }
                : task
            ),
            // Update pending count if status changed from pending to completed/rejected
            pending_count: wasPending
              ? Math.max(0, old.pending_count - 1)
              : old.pending_count
          };
        }
      );

      // Also update detail query if exists
      const previousDetail = queryClient.getQueryData(inboxTaskKeys.detail(id));
      queryClient.setQueryData(inboxTaskKeys.detail(id), (old: InboxTask | undefined) =>
        old ? { ...old, status: newStatus, completedAt: new Date().toISOString() } : old
      );

      // Return context for rollback
      return { previousQueries, previousDetail };
    },

    // 2. Rollback on error
    onError: (err, { id }, context) => {
      if (context?.previousQueries) {
        // Restore all previous query states
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(inboxTaskKeys.detail(id), context.previousDetail);
      }
    },

    // 3. Refetch on success/error to sync with server
    onSettled: (_, __, { id }) => {
      // Invalidate ALL inbox task queries (regardless of filter params)
      queryClient.invalidateQueries({ queryKey: ['inboxTasks'] });
      // Also invalidate detail query
      queryClient.invalidateQueries({ queryKey: inboxTaskKeys.detail(id) });
    }
  });
}

